/**
 * @file oss.ts
 * @description 应用层 CDN / 固件 URL 门面。
 *
 * 渠道 → 区域 → URL 的映射定义在 `scripts/config.mjs`（单一事实来源）。
 * 本文件把业务渠道与构建类型接到 SDK 运行时配置，供业务代码默认无参调用。
 *
 * 设计原则：渠道判断属于应用层职责，SDK 不感知 channel。
 * 应用层在初始化 RapooDeviceManager 时调用本工具，把计算结果通过
 * `RapooDeviceManager.getInstance(manifests, options)` 注入。
 */

import type { RapooDeviceManagerOptions } from '@rapoo/device-sdk'

import {
  regionCdn,
  WEB_DIST_PATH,
  OVERSEAS_CHANNELS,
  getChannelRegion,
  isOverseasChannel as isOverseasChannelByChannel,
  getAssetBaseUrl as getAssetBaseUrlByChannel,
  getFirmwareBaseUrl as getFirmwareBaseUrlByChannel,
  getFirmwareVersionPolicy as getFirmwareVersionPolicyByBuildFlavor,
  getWebDistBaseUrl as getWebDistBaseUrlByChannel,
  getUpdateBaseUrl as getUpdateBaseUrlByChannel,
  type BuildFlavor,
} from '../../../../scripts/config.mjs'

export { regionCdn, WEB_DIST_PATH, OVERSEAS_CHANNELS, getChannelRegion }

export const DOMESTIC_ASSET_BASE_URL: string = regionCdn.cn.asset
export const OVERSEAS_ASSET_BASE_URL: string = regionCdn.global.asset
export const DOMESTIC_FIRMWARE_BASE_URL: string = regionCdn.cn.firmware
export const OVERSEAS_FIRMWARE_BASE_URL: string = regionCdn.global.firmware
export const DOMESTIC_UPDATE_BASE_URL: string = regionCdn.cn.update
export const OVERSEAS_UPDATE_BASE_URL: string = regionCdn.global.update

export function isOverseasChannel(
  channel: string | undefined | null = import.meta.env.VITE_CHANNEL,
): boolean {
  return isOverseasChannelByChannel(channel)
}

export function getFirmwareBaseUrl(channel: string = import.meta.env.VITE_CHANNEL): string {
  return getFirmwareBaseUrlByChannel(channel)
}

export function getFirmwareVersionPolicy(
  buildFlavor: BuildFlavor = import.meta.env.VITE_BUILD_FLAVOR,
) {
  return getFirmwareVersionPolicyByBuildFlavor(buildFlavor)
}

/**
 * 统一构造 SDK 单例首次初始化选项，避免调试页抢先创建单例时遗漏渠道策略。
 */
export function getRapooDeviceManagerOptions(
  options: { firmwareBaseUrl?: string; channel?: string; buildFlavor?: BuildFlavor } = {},
): RapooDeviceManagerOptions {
  const channel = options.channel ?? import.meta.env.VITE_CHANNEL
  const buildFlavor = options.buildFlavor ?? import.meta.env.VITE_BUILD_FLAVOR
  return {
    firmwareBaseUrl: options.firmwareBaseUrl ?? getFirmwareBaseUrl(channel),
    firmwareVersionPolicy: getFirmwareVersionPolicy(buildFlavor),
  }
}

export function getAssetBaseUrl(channel: string = import.meta.env.VITE_CHANNEL): string {
  return getAssetBaseUrlByChannel(channel)
}

export function getWebDistBaseUrl(channel: string = import.meta.env.VITE_CHANNEL): string {
  return getWebDistBaseUrlByChannel(channel)
}

export function getUpdateBaseUrl(channel: string = import.meta.env.VITE_CHANNEL): string {
  return getUpdateBaseUrlByChannel(channel)
}
